import React, { Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react'
import { BrowserRouter as Router, useLocation } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { motion, AnimatePresence } from 'framer-motion'
import { Toaster } from 'react-hot-toast'
import ErrorFallback from './components/common/ErrorFallback'
import GlobalLoader from './components/common/GlobalLoader'
import AppRoutes from './routes/AppRoutes'
import { AuthProvider } from './context/AuthContext'
import { CartProvider } from './context/CartContext'
import { ToastProvider } from './context/ToastContext'
import { FlyingAnimationProvider } from './context/FlyingAnimationContext'
import ScrollToTop from './components/common/ScrollToTop'
import AnnouncementBanner from './pages/home/AnnouncementBanner'


const Header = lazy(() => import('./components/common/Header'))
const Footer = lazy(() => import('./components/common/Footer'))

const HeaderSkeleton = () => null

const AppContent = () => {
  const location = useLocation()
  const { pathname } = location
  const [isLoading, setIsLoading] = useState(false)

  useLayoutEffect(() => {
    setIsLoading(true)
  }, [pathname])

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500)
    return () => clearTimeout(timer)
  }, [pathname])

  const isAuthRoute = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email']
    .includes(pathname)
  const isAdminRoute = pathname.startsWith('/admin')
  const hideHeaderFooter = isAuthRoute || isAdminRoute

  return (
    <>
      <ScrollToTop />
      <GlobalLoader isLoading={isLoading} />
      <AnimatePresence mode="wait">
        <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900 w-full">
          {!hideHeaderFooter && (
            <Suspense fallback={<HeaderSkeleton />}>
              <Header />
            </Suspense>
          )}
          <main className={`flex-grow w-full ${hideHeaderFooter ? 'bg-white' : ''}`}>
            <Suspense fallback={<GlobalLoader isLoading={true} />}>
              <div className="w-full">
                <AppRoutes />
              </div>
            </Suspense>
          </main>
          {!hideHeaderFooter && (
            <Suspense fallback={null}>
              <Footer />
            </Suspense>
          )}
          {!hideHeaderFooter && <AnnouncementBanner />}
        </div>
      </AnimatePresence>
    </>
  )
}

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <FlyingAnimationProvider>
          <ToastProvider>
            <AuthProvider>
              <CartProvider>
                <Toaster position="top-right" />
                <AppContent />
              </CartProvider>
            </AuthProvider>
          </ToastProvider>
        </FlyingAnimationProvider>
      </Router>
    </ErrorBoundary>
  )
}

export default App